import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiPlay, FiPause, FiVolume2, FiVolumeX, FiMaximize, FiSkipBack, FiSkipForward, FiSettings, FiChevronLeft, FiChevronRight, FiList, FiTv, FiArrowLeft } from 'react-icons/fi';
import './TVPlayer.css';

const TVPlayer = () => {
  const params = useParams();
  const showParam = params.showSlug || params.showId;
  const seasonParam = params.seasonNumber;
  const episodeParam = params.episodeNumber;
  const episodeSlugParam = params.episodeSlug || (seasonParam && episodeParam ? `t${seasonParam}-e${episodeParam}` : undefined);
  const navigate = useNavigate();
  const videoRef = useRef(null);
  
  const [episode, setEpisode] = useState(null);
  const [show, setShow] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Estados del reproductor
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [showEpisodeList, setShowEpisodeList] = useState(false);
  const [quality, setQuality] = useState('auto');
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [controlsVisible, setControlsVisible] = useState(true);

  useEffect(() => {
    loadEpisodeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showParam, episodeSlugParam]);

  useEffect(() => {
    // If user landed on the legacy numeric URL, replace it with the canonical slug URL.
    if (params.showSlug) return;
    if (!show?.slug || !episode) return;
    const canonicalEpisodeSlug = episode.episode_slug || episode.slug || `t${episode.season_number}-e${episode.episode_number}`;
    navigate(`/tv/watch/${show.slug}/${canonicalEpisodeSlug}`, { replace: true });
  }, [params.showSlug, show?.slug, episode, navigate]);

  useEffect(() => {
    let timeout;
    const hideControls = () => {
      if (isPlaying) {
        timeout = setTimeout(() => setControlsVisible(false), 3000);
      }
    };
    
    hideControls();
    return () => clearTimeout(timeout);
  }, [isPlaying, controlsVisible]);

  const handleMouseMove = () => {
    setControlsVisible(true);
  };

  const loadEpisodeData = async () => {
    try {
      const res = await fetch(`http://localhost:5000/api/tv/watch/${showParam}/${episodeSlugParam}`);
      const data = await res.json();
      setEpisode(data.episode);
      setShow(data.show);
      setEpisodes(data.seasonEpisodes || []);
    } catch (error) {
      console.error('Error loading episode:', error);
      setShow(null);
      setEpisode(null);
      setEpisodes([]);
    } finally {
      setLoading(false);
    }
  };

  // Controles del reproductor
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const current = videoRef.current.currentTime;
      const dur = videoRef.current.duration;
      setCurrentTime(current);
      setDuration(dur);
      setProgress(dur ? (current / dur) * 100 : 0);
    }
  };

  const handleProgressClick = (e) => {
    if (videoRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = pos * videoRef.current.duration;
    }
  };

  const handleFullscreen = () => {
    const playerContainer = document.querySelector('.tv-player-container');
    if (playerContainer) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        playerContainer.requestFullscreen();
      }
    }
  };

  const skip = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime += seconds;
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatEpisodeDuration = (rawDuration) => {
    if (rawDuration === null || rawDuration === undefined) return '';
    if (typeof rawDuration === 'string') return rawDuration;
    return formatTime(rawDuration);
  };

  const changePlaybackSpeed = (speed) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = speed;
      setPlaybackSpeed(speed);
    }
  };

  const canonicalShowSlug = show?.slug || showParam;

  const getEpisodeSlugFromList = (ep) => ep.episode_slug || ep.slug || `t${ep.season_number}-e${ep.episode_number}`;

  const goToNextEpisode = () => {
    if (!episode) return;
    const nextEp = episodes.find(ep => ep.episode_number === episode.episode_number + 1);
    if (nextEp) {
      navigate(`/tv/watch/${canonicalShowSlug}/${getEpisodeSlugFromList(nextEp)}`);
    }
  };

  const goToPrevEpisode = () => {
    if (!episode) return;
    const prevEp = episodes.find(ep => ep.episode_number === episode.episode_number - 1);
    if (prevEp) {
      navigate(`/tv/watch/${canonicalShowSlug}/${getEpisodeSlugFromList(prevEp)}`);
    }
  };

  const qualities = ['auto', '1080p', '720p', '480p', '360p'];
  const speeds = [0.5, 0.75, 1, 1.25, 1.5, 2];

  if (loading) {
    return (
      <div className="tv-player-loading">
        <div className="loading-spinner">
          <FiTv />
        </div>
        <p>Cargando...</p>
      </div>
    );
  }

  const currentEpNum = episode?.episode_number;
  const hasNextEpisode = typeof currentEpNum === 'number' && episodes.some(ep => ep.episode_number === currentEpNum + 1);
  const hasPrevEpisode = typeof currentEpNum === 'number' && episodes.some(ep => ep.episode_number === currentEpNum - 1);

  const videoUrl = episode?.video_url && episode.video_url !== '#' ? episode.video_url : null;

  return (
    <div className="tv-player-page">
      {/* Reproductor principal */}
      <div 
        className={`tv-player-container ${controlsVisible ? 'controls-visible' : ''}`}
        onMouseMove={handleMouseMove}
      >
        {videoUrl ? (
          <video
            ref={videoRef}
            src={videoUrl}
            onTimeUpdate={handleTimeUpdate}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onLoadedMetadata={() => setDuration(videoRef.current?.duration || 0)}
            onClick={togglePlay}
            autoPlay
          />
        ) : (
          <div className="tv-player-loading">
            <div className="loading-spinner">
              <FiTv />
            </div>
            <p>Video no disponible para este episodio.</p>
            <Link to={`/tv/show/${canonicalShowSlug}`} className="back-to-tv" style={{ marginTop: 12 }}>
              <FiArrowLeft />
              <span>Volver al programa</span>
            </Link>
          </div>
        )}

        {/* Overlay de controles */}
        <div className={`player-controls-overlay ${controlsVisible ? 'visible' : ''}`}>
          {/* Barra superior */}
          <div className="player-top-bar">
            <Link to="/tv" className="back-to-tv">
              <FiArrowLeft />
              <span>Volver a GRAPHYRA TV</span>
            </Link>
            
            <div className="player-logo">
              <FiTv /> GRAPHYRA TV
            </div>
          </div>

          {/* Centro - Play grande */}
          <div className="player-center">
            {!isPlaying && (
              <button className="big-play-btn" onClick={togglePlay}>
                <FiPlay />
              </button>
            )}
          </div>

          {/* Barra inferior de controles */}
          <div className="player-bottom-bar">
            {/* Info del episodio */}
            <div className="episode-info-bar">
              <span className="show-title">{show?.title}</span>
              <span className="episode-title">
                T{episode?.season_number ?? seasonParam} E{episode?.episode_number ?? episodeParam}: {episode?.title}
              </span>
            </div>

            {/* Barra de progreso */}
            <div className="progress-bar-container">
              <span className="time-current">{formatTime(currentTime)}</span>
              <div className="progress-bar" onClick={handleProgressClick}>
                <div className="progress-filled" style={{ width: `${progress}%` }}></div>
                <div className="progress-thumb" style={{ left: `${progress}%` }}></div>
              </div>
              <span className="time-duration">{formatTime(duration)}</span>
            </div>

            {/* Controles */}
            <div className="controls-row">
              <div className="controls-left">
                <button 
                  className="control-btn" 
                  onClick={goToPrevEpisode}
                  disabled={!hasPrevEpisode}
                  title="Episodio anterior"
                >
                  <FiChevronLeft />
                </button>
                <button className="control-btn" onClick={() => skip(-10)} title="Retroceder 10s">
                  <FiSkipBack />
                </button>
                <button className="control-btn play-pause-btn" onClick={togglePlay}>
                  {isPlaying ? <FiPause /> : <FiPlay />}
                </button>
                <button className="control-btn" onClick={() => skip(10)} title="Adelantar 10s">
                  <FiSkipForward />
                </button>
                <button 
                  className="control-btn" 
                  onClick={goToNextEpisode}
                  disabled={!hasNextEpisode}
                  title="Siguiente episodio"
                >
                  <FiChevronRight />
                </button>
                <button className="control-btn" onClick={toggleMute}>
                  {isMuted ? <FiVolumeX /> : <FiVolume2 />}
                </button>
              </div>

              <div className="controls-right">
                {/* Lista de episodios */}
                <div className="episodes-dropdown">
                  <button 
                    className="control-btn" 
                    onClick={() => setShowEpisodeList(!showEpisodeList)}
                    title="Lista de episodios"
                  >
                    <FiList />
                  </button>
                  
                  {showEpisodeList && (
                    <div className="episodes-menu">
                      <div className="episodes-menu-header">
                        <span>Temporada {episode?.season_number ?? seasonParam}</span>
                      </div>
                      <div className="episodes-list">
                        {episodes.map(ep => (
                          <Link
                            key={ep.id}
                            to={`/tv/watch/${canonicalShowSlug}/${getEpisodeSlugFromList(ep)}`}
                            className={`episode-item ${ep.episode_number === currentEpNum ? 'active' : ''}`}
                            onClick={() => setShowEpisodeList(false)}
                          >
                            <span className="ep-number">E{ep.episode_number}</span>
                            <span className="ep-title">{ep.title}</span>
                            <span className="ep-duration">{formatEpisodeDuration(ep.duration)}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Configuración */}
                <div className="settings-dropdown">
                  <button 
                    className="control-btn" 
                    onClick={() => setShowSettings(!showSettings)}
                    title="Configuración"
                  >
                    <FiSettings />
                  </button>
                  
                  {showSettings && (
                    <div className="settings-menu">
                      <div className="settings-section">
                        <span className="settings-label">Calidad</span>
                        <div className="settings-options">
                          {qualities.map(q => (
                            <button 
                              key={q}
                              className={`settings-option ${quality === q ? 'active' : ''}`}
                              onClick={() => setQuality(q)}
                            >
                              {q === 'auto' ? 'Auto' : q}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      <div className="settings-section">
                        <span className="settings-label">Velocidad</span>
                        <div className="settings-options">
                          {speeds.map(s => (
                            <button 
                              key={s}
                              className={`settings-option ${playbackSpeed === s ? 'active' : ''}`}
                              onClick={() => changePlaybackSpeed(s)}
                            >
                              {s === 1 ? 'Normal' : `${s}x`}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <button className="control-btn" onClick={handleFullscreen} title="Pantalla completa">
                  <FiMaximize />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Info debajo del reproductor */}
      <div className="tv-player-info">
        <div className="info-container">
          <div className="info-main">
            <h1>{show?.title}</h1>
            <h2>Temporada {episode?.season_number ?? seasonParam}, Episodio {episode?.episode_number ?? episodeParam}: {episode?.title}</h2>
            <p className="episode-description">{episode?.description}</p>
            
            <div className="episode-meta">
              <span className="category-tag">{show?.category}</span>
              <span className="duration">Duración: {formatTime(duration)}</span>
            </div>
          </div>

          {/* Siguiente episodio */}
          {hasNextEpisode && (
            <div className="next-episode-card">
              <span className="next-label">Siguiente episodio</span>
              <Link
                to={`/tv/watch/${canonicalShowSlug}/${getEpisodeSlugFromList(episodes.find(ep => ep.episode_number === currentEpNum + 1) || {})}`}
                className="next-episode-link"
              >
                <div className="next-ep-info">
                  <span className="next-ep-number">E{currentEpNum + 1}</span>
                  <span className="next-ep-title">
                    {episodes.find(ep => ep.episode_number === currentEpNum + 1)?.title}
                  </span>
                </div>
                <FiChevronRight />
              </Link>
            </div>
          )}
        </div>

        {/* Lista de episodios de la temporada */}
        <div className="season-episodes">
          <h3>Todos los episodios - Temporada {episode?.season_number ?? seasonParam}</h3>
          <div className="episodes-grid">
            {episodes.map(ep => (
              <Link
                key={ep.id}
                to={`/tv/watch/${canonicalShowSlug}/${getEpisodeSlugFromList(ep)}`}
                className={`episode-card ${ep.episode_number === currentEpNum ? 'current' : ''}`}
              >
                <div className="episode-thumbnail">
                  <span className="ep-badge">E{ep.episode_number}</span>
                  {ep.episode_number === currentEpNum && (
                    <div className="now-playing">
                      <FiPlay /> Reproduciendo
                    </div>
                  )}
                </div>
                <div className="episode-card-info">
                  <span className="episode-card-title">{ep.title}</span>
                  <span className="episode-card-duration">{formatTime(ep.duration)}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Ver más de la serie */}
        <div className="show-more">
          <Link to={`/tv/show/${canonicalShowSlug}`} className="show-more-link">
            Ver todas las temporadas de {show?.title}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TVPlayer;
